import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Transfer,

} from 'antd';

import type { TransferProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { UnpaidList } from "../../../../types/UnpaidList";

interface TailorInvoiceModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: UnpaidList | null;
    isEditMode?: boolean;
    tailorinvoicedata: UnpaidList[];
}

const TailorInvoiceModal: React.FC<TailorInvoiceModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    tailorinvoicedata,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [ledgerList, setLedgerList] = useState<{ ledger_id: string, ledger_name: string }[]>([]);

    const [unpaidlists, setUnpaidlists] = useState<UnpaidList[]>([]);

    const [targetKeys, setTargetKeys] = useState<React.Key[]>([]);



    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    const handleChange: TransferProps['onChange'] = (newTargetKeys, direction, moveKeys) => {
        console.log(newTargetKeys, direction, moveKeys);
        setTargetKeys(newTargetKeys);
    };

    const fetchUnpaidInvoice = async (ledgerId: string) => {
        try {
            const data = await apiRequest<UnpaidList[]>({
                method: 'get',
                url: `unpaid-invoice-list?ledger_id=${ledgerId}`,
            });
            const formatted = data.map((item) => ({
                ...item,
                key: String(item.sd_id),       // ✅ REQUIRED for Transfer
            }));
            setUnpaidlists(formatted);
        } catch (error) {
            showError('Failed to fetch body shape ');
            console.error(error);
        }
    };

    // Handle form submit
    const handleAddUnpaidInvoice = async (values: any) => {
        try {
            const formData = new FormData();

            formData.append('ledger_id', values.ti_ledger_id);
            formData.append('selected_invoices', JSON.stringify(targetKeys));


            if (initialValues?.ji_id) {
                formData.append('ji_id', initialValues.ji_id.toString());
            }
            const url = 'upsert-tailor-invoice';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: formData,
            });
            console.log(formData);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'tailor invoice updated successfully!' : 'tailor invoice created successfully!',
            );
            form.resetFields();


            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} body shape`);
        }
    };

    const fetchLedgerList = async () => {
        try {
            const data = await apiRequest<{ ledgers: any[] }>({
                method: 'get',
                url: 'ledger-data', // or your endpoint
            });
            setLedgerList(data.ledgers.map(l => ({
                ledger_id: l.ledger_id,
                ledger_name: `${l.ledger_first_name} ${l.ledger_last_name}`
            })));
        } catch (error) {
            showError('Failed to fetch ledgers');
        }
    };
    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                ti_ledger_id: initialValues.ti_ledger_id,
            });

        } else if (visible) {
            form.resetFields();
        }
        if (visible) fetchLedgerList();
    }, [visible, isEditMode, initialValues, form]);
    const renderItem = (item: UnpaidList) => {
        const customLabel = (
            <span className="custom-item">
                {item.sd_id} - {item.sd_ledger_id}
            </span>
        );

        return {
            label: customLabel, // for displayed item
            value: item.sd_id, // for title and filter matching
        };
    };
    return (
        <>
            <Modal
                title={isEditMode ? 'Edit Tailor Invoice' : 'Add New Tailor Invoice'}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                width="90%"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleAddUnpaidInvoice}
                    autoComplete="off"
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="ti_ledger_id"
                                label={
                                    <span>
                                        Ledger &nbsp;
                                    </span>
                                }
                                rules={[{ required: true, message: 'Please select Ledger' }]}
                            >
                                <Select
                                    placeholder="Select Ledger"
                                    showSearch
                                    optionFilterProp="children"
                                    onChange={(value) => fetchUnpaidInvoice(value)}
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {ledgerList.map((ledger) => (
                                        <Select.Option key={ledger.ledger_id} value={ledger.ledger_id}>
                                            {ledger.ledger_name}
                                        </Select.Option>
                                    ))}
                                </Select>

                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Transfer
                                dataSource={unpaidlists}
                                targetKeys={targetKeys}
                                onChange={handleChange}
                                render={(item) => (
                                    <span>
                                        {item.sd_id} - {item.sd_ledger_id}
                                    </span>
                                )}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <Form.Item >
                            <Space>
                                <Button onClick={onCancel}>Cancel</Button>
                                {!isEditMode && <Button
                                    type="primary"
                                    htmlType="submit"
                                    onClick={() => setIsModalClose(false)}
                                    disabled={isSubmitDisabled()}
                                >
                                    Submit & Create New Tailor Invoice
                                </Button>}
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    onClick={() => setIsModalClose(true)}
                                    disabled={isSubmitDisabled()}
                                >
                                    Submit
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default TailorInvoiceModal;
